import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { envConfig } from '../../env-config';

interface LocalServiceEntry {
  repo: string;
  pathPrefix: string;
  // Opcional — cuando varios lambdas comparten el mismo pathPrefix pero se
  // distinguen por el tramo final de la ruta (ej. /status vs /progress vs
  // /reset, con un {assignmentId} dinámico en el medio), exige que la url
  // también termine en este sufijo.
  pathSuffix?: string;
  port: number;
}

interface LocalServicesFile {
  services: LocalServiceEntry[];
}

interface AppConfig {
  VITE_COGNITO_REGION: string;
  VITE_COGNITO_USER_POOL_ID: string;
  VITE_COGNITO_CLIENT_ID: string;
  VITE_COGNITO_DOMAIN: string;
  VITE_COGNITO_REDIRECT_URI: string;
  VITE_API_KEY: string;
  VITE_API_BASE_URL: string;
  VITE_FACE_DETECTION_ENABLED?: string;
  // VC detection (Meet/Zoom/Teams durante la prueba) — CONTRATO-VC-DETECTION.md §3.
  // Kill-switch aparte del override remoto (VerifyTest.conferencing_detection_enabled),
  // mismo patrón que VITE_FACE_DETECTION_ENABLED: default seguro = habilitado.
  VITE_CONFERENCING_DETECTION_ENABLED?: string;
  // Client voice metrics to RUM (fc_client_metric). Off unless 'true' (config.json of the Release).
  VITE_DD_CLIENT_METRICS_ENABLED?: string;
  // Dev local — salta el onboarding de full_conversation e inicia directo con
  // Eva. Nunca presente en config.json de ambientes reales (deploy/config-sync).
  VITE_SKIP_START?: string;
  // Sandbox de componentes (/admin/test-components). Nunca presente en
  // config.json de ambientes reales — solo el .env local lo setea a 'true'.
  VITE_TEST_COMPONENTS_VIEW?: string;
  // Clave publica VAPID para suscripciones Web Push (PushNotificationService).
  // No es secreta -- se expone al navegador a proposito.
  VITE_VAPID_PUBLIC_KEY?: string;
  // Panel de monitoreo dev-only (afecto/pace/adaptación). Vacío por defecto
  // en cualquier build real — solo el Release Pipeline de dev y el .env local
  // lo setean a 'dev'. Ver getter `stage` más abajo para el porqué del default.
  VITE_STAGE?: string;
  // Ola 3 — flag de rollout barge-in. Default false (Stage 1).
  // Override en QA sin redeploy: localStorage.setItem('fcBargeInEnabled','true').
  fc_bargein_enabled?: boolean;
  // Ola 4 — flag de visibilidad del panel de transcripción en vivo.
  // Default ausente=false (oculto): el candidato no debe ver la transcripción en vivo.
  // Override en QA sin redeploy: localStorage.setItem('fcShowTranscript','true').
  fc_show_transcript?: boolean;
  // Paso 11 — gate de movimiento del recorrido de espacio de trabajo. Vacíos
  // por defecto (ver getters más abajo para el valor real usado).
  VITE_WORKSPACE_SCAN_INTERVAL_MS?: string;
  VITE_WORKSPACE_SCAN_MOVEMENT_TICKS?: string;
  VITE_WORKSPACE_SCAN_DIFF_THRESHOLD?: string;
  // Umbrales mínimos de red — ver getters networkPingThresholdMs/networkPingWarnMs/
  // networkBadPingLimit más abajo para el porqué.
  VITE_NETWORK_PING_THRESHOLD_MS?: string;
  VITE_NETWORK_PING_WARN_MS?: string;
  VITE_NETWORK_BAD_PING_LIMIT?: string;
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private config: AppConfig = { ...envConfig };
  // Dev local — repos con lambda corriendo en `sam local start-api`, para que
  // el front les pegue a ellos en vez de al API Gateway de dev. Archivo
  // gitignored (src/assets/local-services.json), ausente en cualquier build real.
  private localServices: LocalServiceEntry[] = [];
  // El APP_INITIALIZER de esta config y el inicializador interno del Router
  // (initialNavigation: 'enabledBlocking') corren en paralelo -- Angular NO
  // garantiza que load() termine antes de que un guard dispare su primera
  // request. whenReady() deja que un guard/servicio espere explicitamente.
  private readyResolve!: () => void;
  private readyPromise: Promise<void> = new Promise((resolve) => { this.readyResolve = resolve; });

  constructor(private http: HttpClient) {}

  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  async load(): Promise<void> {
    try {
      // timeout(5s): si config.json esta lento (CloudFront frio), caer rapido al
      // fallback env-config en vez de bloquear el APP_INITIALIZER.
      const runtime = await firstValueFrom(
        this.http.get<AppConfig>('/assets/config.json').pipe(timeout(5000))
      );
      // Solo usa config.json si tiene valores reales (producción)
      if (runtime?.VITE_API_BASE_URL) {
        this.config = runtime;
        console.log('Config loaded from config.json:', this.config.VITE_API_BASE_URL);
      } else {
        console.log('Config loaded from env-config.ts:', this.config.VITE_API_BASE_URL);
      }
    } catch {
      console.log('Config loaded from env-config.ts (fallback):', this.config.VITE_API_BASE_URL);
    }

    await this.loadLocalServices();
    this.readyResolve();
  }

  private async loadLocalServices(): Promise<void> {
    try {
      const local = await firstValueFrom(
        this.http.get<LocalServicesFile>('/assets/local-services.json').pipe(timeout(2000))
      );
      this.localServices = local?.services ?? [];
      if (this.localServices.length) {
        console.log('Local services registry loaded:', this.localServices);
      }
    } catch {
      // Archivo ausente (todo build real, o dev sin nada registrado) — sin overrides.
      this.localServices = [];
    }
  }

  // Si `url` cae bajo un pathPrefix registrado en local-services.json, devuelve
  // la url reescrita apuntando a `http://localhost:<port>` en vez de apiBaseUrl.
  // Si no hay match, devuelve null (la request sigue a dev sin cambios).
  resolveLocalOverride(url: string): string | null {
    if (!this.localServices.length) return null;
    for (const service of this.localServices) {
      const remotePrefix = `${this.apiBaseUrl}${service.pathPrefix}`;
      if (!url.startsWith(remotePrefix)) continue;
      // Match en borde de segmento: exacto, o seguido de "/" o "?" — sin esto,
      // un pathPrefix como "/api/v2/admin/test" también matchea de más
      // "/api/v2/admin/test-records" (mismo prefijo de texto, recurso distinto).
      const nextChar = url.charAt(remotePrefix.length);
      if (nextChar !== '' && nextChar !== '/' && nextChar !== '?') continue;
      // Cuando varios lambdas comparten el mismo pathPrefix pero se distinguen
      // por el tramo final de la ruta (ej. /status vs /progress vs /reset,
      // con un {assignmentId} dinámico en el medio), exige que la url también
      // termine en pathSuffix.
      if (service.pathSuffix && !url.endsWith(service.pathSuffix)) continue;
      return `http://localhost:${service.port}${url.slice(this.apiBaseUrl.length)}`;
    }
    return null;
  }

  // Cognito
  get region() { return this.config.VITE_COGNITO_REGION; }
  get userPoolId() { return this.config.VITE_COGNITO_USER_POOL_ID; }
  get clientId() { return this.config.VITE_COGNITO_CLIENT_ID; }
  get cognitoDomain() { return this.config.VITE_COGNITO_DOMAIN; }
  get cognitoRedirectUri() { return this.config.VITE_COGNITO_REDIRECT_URI; }
  
  // API
  get apiKey() { return this.config.VITE_API_KEY; }
  get apiBaseUrl() { return this.config.VITE_API_BASE_URL; }
  get faceDetectionEnabled() { return this.config.VITE_FACE_DETECTION_ENABLED !== 'false'; }
  get clientMetricsEnabled(): boolean { return this.config.VITE_DD_CLIENT_METRICS_ENABLED === 'true'; }
  get skipStart(): boolean { return this.config.VITE_SKIP_START?.toLowerCase() === 'true'; }
  get testComponentsView(): boolean { return this.config.VITE_TEST_COMPONENTS_VIEW?.toLowerCase() === 'true'; }
  get vapidPublicKey(): string { return this.config.VITE_VAPID_PUBLIC_KEY || ''; }

  // URLs Quiz/Pruebas
  get urlObtenerPreguntas() { return `${this.apiBaseUrl}/api/v2/test`; }
  /** FC quality_mode=low — HTTP full-conversation-low / fc-low API (boot / submit_turn). Not used by high/AgentCore. */
  get urlFcLow() { return `${this.apiBaseUrl}/api/v2/fc/low`; }
  /** Motor de decisión estructurada (Nova Micro tool-use). Clasifica comandos de voz del piense. */
  get urlDecisions() { return `${this.apiBaseUrl}/api/v2/decisions`; }
  get urlVerificarPrueba() { return `${this.apiBaseUrl}/api/v2/test/verify`; }
  get urlGeneratePresignedUrl() { return `${this.apiBaseUrl}/api/v2/photos/add`; }
  get urlRecordingsPresigned() { return `${this.apiBaseUrl}/api/v2/recordings/presigned`; }
  get urlFinishTest() { return `${this.apiBaseUrl}/api/v2/test/finish`; }
  get urlSendQuestionProgress() { return `${this.apiBaseUrl}/api/v2/answers-progress`; }
  get urlFeedback() { return `${this.apiBaseUrl}/api/v2/feedback`; }

  // URLs Admin
  get urlSendTest() { return `${this.apiBaseUrl}/api/v2/admin/send-test`; }
  get urlGetRecordSendTest() { return `${this.apiBaseUrl}/api/v2/admin/test-records`; }
  get urlGetAllTags() { return `${this.apiBaseUrl}/api/v2/admin/tags`; }
  get urlAdminTests() { return `${this.apiBaseUrl}/api/v2/admin/test`; }
  get urlAdminAuthMe() { return `${this.apiBaseUrl}/api/v2/admin/auth/me`; }
  get urlAdminDashboardStats() { return `${this.apiBaseUrl}/api/v2/admin/dashboard/stats`; }
  get urlAdminCandidates() { return `${this.apiBaseUrl}/api/v2/admin/candidates`; }
  get urlAdminReports() { return `${this.apiBaseUrl}/api/v2/admin/reports`; }
  get urlAdminKnowledgeAreas() { return `${this.apiBaseUrl}/api/v2/admin/knowledge-areas`; }
  get urlAdminAccounts() { return `${this.apiBaseUrl}/api/v2/admin/accounts`; }
  get urlAdminUsageMetrics() { return `${this.apiBaseUrl}/api/v2/admin/usage-metrics`; }
  get urlAdminAssignments() { return `${this.apiBaseUrl}/api/v2/admin/assignments`; }
  // Public (no auth). Must stay outside '/admin/' or the auth interceptor sends the admin token.
  get urlMaintenanceStatus() { return `${this.apiBaseUrl}/api/v2/maintenance/status`; }
  get urlAdminMaintenance() { return `${this.apiBaseUrl}/api/v2/admin/maintenance`; }
  get urlAdminUsers() { return `${this.apiBaseUrl}/api/v2/admin/users`; }
  get urlAdminProfiles() { return `${this.apiBaseUrl}/api/v2/admin/profiles`; }
  get urlAdminConfigurations() { return `${this.apiBaseUrl}/api/v2/admin/configurations`; }
  get urlAdminProcess() { return `${this.apiBaseUrl}/api/v2/admin/process`; }
  get urlAdminProcessRecords() { return `${this.apiBaseUrl}/api/v2/admin/process-records`; }
  get urlAdminSparring() { return `${this.apiBaseUrl}/api/v2/admin/sparring`; }
  get urlSparringBot() { return `${this.apiBaseUrl}/api/v2/sparring/bot`; }
  get urlProcessState() { return `${this.apiBaseUrl}/api/v2/process`; }
  get urlInvokeFlow() { return `${this.apiBaseUrl}/api/v2/sqs/add`; }
  get urlSendTestPublic() { return `${this.apiBaseUrl}/api/v2/send-test`; }
  get urlComposeTest() { return `${this.apiBaseUrl}/api/v2/compose-test`; }
  urlPreTestState(testId: string) { return `${this.apiBaseUrl}/api/v2/test/${testId}/pre-test-state`; }
  urlPreTestStep(testId: string) { return `${this.apiBaseUrl}/api/v2/test/${testId}/pre-test-step`; }
  get urlArtifactReviewAnalyze() { return `${this.apiBaseUrl}/api/v2/artifact-review/analyze`; }
  urlAnswersProgress(id: string) { return `${this.apiBaseUrl}/api/v2/answers-progress/${id}`; }
  get urlConversationalPresign() { return `${this.apiBaseUrl}/api/v2/conversational/presign`; }
  get urlSaveAlertReport() { return `${this.apiBaseUrl}/api/v2/alerts`; }
  get objectDetectionEnabled() { return this.config.VITE_FACE_DETECTION_ENABLED !== 'false'; }
  get voiceDetectionEnabled() { return this.config.VITE_FACE_DETECTION_ENABLED !== 'false'; }
  // Kill-switch local de VC detection — el gate remoto real (por prueba/global)
  // llega en VerifyTest, este solo tapa el feature entero a nivel build/env.
  get conferencingDetectionEnabled() { return this.config.VITE_CONFERENCING_DETECTION_ENABLED !== 'false'; }

  // Paso 11 — gate de movimiento del recorrido de espacio de trabajo.
  get workspaceScanIntervalMs(): number {
    return Number(this.config.VITE_WORKSPACE_SCAN_INTERVAL_MS) || 1500;
  }
  get workspaceScanMovementTicks(): number {
    return Number(this.config.VITE_WORKSPACE_SCAN_MOVEMENT_TICKS) || 3;
  }
  get workspaceScanDiffThreshold(): number {
    return Number(this.config.VITE_WORKSPACE_SCAN_DIFF_THRESHOLD) || 15;
  }

  // Umbrales mínimos de red — hoy eran magic numbers repartidos entre
  // network-quality.service.ts (250 ms) y fc-session.component.ts (3 fallos
  // seguidos). Acá quedan en un solo lugar y se pueden mover por env sin
  // redeploy de código.
  get networkPingThresholdMs(): number { return Number(this.config.VITE_NETWORK_PING_THRESHOLD_MS) || 250; }
  // Acotado al umbral duro: un warn POR ENCIMA del threshold nunca dispararía
  // el tier 'marginal' (por encima del threshold el status ya es 'fail'), y la
  // mala config quedaría en silencio.
  get networkPingWarnMs(): number {
    return Math.min(Number(this.config.VITE_NETWORK_PING_WARN_MS) || 150, this.networkPingThresholdMs);
  }
  get networkBadPingLimit(): number { return Number(this.config.VITE_NETWORK_BAD_PING_LIMIT) || 3; }

  // Fase 20 E.4 — kill-switch for photo upload Web Worker.
  // Set to false to force all photo uploads through the main-thread HTTP fallback.
  get photoUploadWorkerEnabled(): boolean { return true; }

  // URLs Identity Verification
  get urlUploadIdentityDocument() { return `${this.apiBaseUrl}/api/v2/identity/document/upload`; }
  get urlConfirmIdentity() { return `${this.apiBaseUrl}/api/v2/identity/confirm`; }

  // URLs Admin AI
  get urlCreateTestPlanDraft() { return `${this.apiBaseUrl}/api/v2/admin/test-plan-drafts`; }
  urlGetTestPlanDraft(draftId: string) { return `${this.apiBaseUrl}/api/v2/admin/test-plan-drafts/${draftId}`; }
  get urlAdminFeedback() { return `${this.apiBaseUrl}/api/v2/admin/feedback`; }
  get urlAdminAlertReports() { return `${this.apiBaseUrl}/api/v2/admin/alerts`; }

  // Notificaciones estandarizadas (ver PLAN_notificaciones-estandarizado.md) —
  // 1 sola ruta de registro, compartida entre admin/valorador y candidato; el
  // tipo se resuelve en el backend consultando Cognito, no por la URL.
  get urlPushSubscriptions() { return `${this.apiBaseUrl}/api/v2/push-subscriptions`; }

  // Trazabilidad y reset de pruebas (ver PLAN_trazabilidad-pruebas.md)
  urlAdminTestStatus(assignmentId: string) { return `${this.apiBaseUrl}/api/v2/admin/test-records/${assignmentId}/status`; }
  urlAdminTestProgress(assignmentId: string) { return `${this.apiBaseUrl}/api/v2/admin/test-records/${assignmentId}/progress`; }
  urlAdminTestReset(assignmentId: string) { return `${this.apiBaseUrl}/api/v2/admin/test-records/${assignmentId}/reset`; }

  // Ola 3 — Barge-in rollout flag.
  // Lectura única en connect() del service: localStorage override gana sobre config.json
  // para habilitar QA sin redeploy. Calculado una vez; no reactivo.
  get bargeInEnabled(): boolean {
    // localStorage override permite activar en QA sin redeploy.
    const override = localStorage.getItem('fcBargeInEnabled');
    if (override !== null) {
      return override === 'true';
    }
    return this.config.fc_bargein_enabled === true;
  }

  // Ola 4 — Visibilidad del panel de transcripción en vivo.
  // El candidato no debe ver la transcripción en vivo; flag para QA.
  // localStorage override permite activar en QA sin redeploy.
  get showTranscriptEnabled(): boolean {
    const override = localStorage.getItem('fcShowTranscript');
    if (override !== null) {
      return override === 'true';
    }
    return this.config.fc_show_transcript === true;
  }

  // Override de QA: fuerza la rama "remota" (presign contra el backend real)
  // en fc-session.service.ts aunque se esté en localhost, para no tener que
  // levantar el agente Python de full_conversation en local.
  // Activar: localStorage.setItem('fcForceRemoteAgent', 'true').
  get forceRemoteAgentEnabled(): boolean {
    return localStorage.getItem('fcForceRemoteAgent') === 'true';
  }

  // Panel de monitoreo dev-only — gate por stage.
  get stage(): string {
    return (this.config.VITE_STAGE ?? '').trim().toLowerCase();
  }

  /** Panel de monitoreo. El override de localStorage es ASIMÉTRICO: puede
   *  apagar, nunca prender. `bargeInEnabled` y `showTranscriptEnabled` usan
   *  override simétrico porque existen para *habilitar* features en QA. Acá
   *  el requisito es el opuesto — que nunca aparezca en producción. Un
   *  override simétrico sería una llave para abrir un panel de diagnóstico
   *  con datos de prosodia encima de la entrevista de un candidato real. El
   *  gate de stage NO es anulable desde el cliente: si `stage !== 'dev'` se
   *  retorna `false` antes de siquiera mirar `localStorage`. */
  get devMonitorEnabled(): boolean {
    if (this.stage !== 'dev') return false;
    try { return localStorage.getItem('fcDevMonitor') !== 'false'; }
    catch { return true; }   // SSR: no hay localStorage
  }
}
